import type { NextPage } from "next";

import classNames from "classnames/bind";
import Breadcrumbs from "common/blocks/Breadcrumbs";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import Image from "common/blocks/Image";
import Layout from "common/layout/Layout";
import ModalProvider from "common/providers/modal/modalProvider";
import { useFavouritesContext } from "features/favourites/hooks/useFavouritesContext";
import RecommendedVehicles from "features/vehicleDetails/components/RecommendedVehicles";
import { DEFAULT_IMAGE_URL } from "lib/constants";
import { ResolvedBaseModel } from "models/baseModel";
import { PDPModelProps } from "models/pdpModel";
import { VehicleService } from "services/vehicle/vehicle";
import slugify from "slugify";

import styles from "./VehicleNotFound.module.scss";

export type ResolvedVehicleNotAvailableProps = ResolvedBaseModel &
    Omit<PDPModelProps, "dynamicSavingsSidebar" | "meta" | "widgetRowcontent">;

const cx = classNames.bind(styles);

const VehicleNotFound: NextPage<ResolvedVehicleNotAvailableProps> = (props) => {
    const { fourTenErrorContent, page, vehicle } = props;
    const { useShortlist } = useFavouritesContext();
    const shortlist = useShortlist();
    const formattedVehicle = page.formattedMakeModel;

    const contactUsCta = fourTenErrorContent?.contactUsCta[0];
    const browseUrl = `${process.env.NEXT_PUBLIC_APP_URL}/used-cars/${slugify(
        formattedVehicle?.make,
        { lower: true }
    )}/${slugify(formattedVehicle?.model, { lower: true })}`;
    const enquireUrl = `${process.env.NEXT_PUBLIC_APP_URL}${contactUsCta?.link}`;
    const image = vehicle.Imagery.MarketingImages[0];
    const imagesSorted = image?.SizedImages?.sort((a, b) => b.Format.localeCompare(a.Format));

    return (
        <Layout {...props} meta={props.fourTenErrorContent.meta}>
            <div className={cx("container", "breadcrumbs")}>
                <div className={cx("row")}>
                    <Breadcrumbs breadcrumbs={props.breadcrumbs} />
                </div>
            </div>
            <div className={cx("content")}>
                <div className={cx("errorContainer")}>
                    <div className={cx("vehicleContainer")}>
                        <div className={cx("vehicleDetails")}>
                            <div className={cx("heading")}>
                                {formattedVehicle.make} {formattedVehicle.model}
                            </div>
                            <div className={cx("subheading")}>
                                {vehicle.Taxonomy.Trim} {vehicle.Taxonomy.Variant}
                            </div>
                        </div>
                        <div className={cx("imageWrapper")}>
                            {imagesSorted != null && imagesSorted?.length > 3 ? (
                                <picture className={cx("pictureWrapper")}>
                                    {imagesSorted.map((i, index) => {
                                        let mediaQueryString: string;
                                        switch (i.Size) {
                                            case "Large":
                                                mediaQueryString =
                                                    "(max-width: 743px), (min-width: 1024px)";
                                                break;
                                            case "Medium":
                                                mediaQueryString =
                                                    "(min-width: 744px) and (max-width: 1023px)";
                                                break;
                                            default:
                                                return;
                                        }
                                        return (
                                            <source
                                                key={index}
                                                media={mediaQueryString}
                                                srcSet={i.Uri}
                                                type={`image/${i.Format}`}
                                            />
                                        );
                                    })}
                                    <img
                                        src={image?.Uri || DEFAULT_IMAGE_URL}
                                        alt={image?.Description}
                                        className={cx("image")}
                                        loading="eager"
                                    />
                                </picture>
                            ) : (
                                <Image
                                    src={image?.Uri || DEFAULT_IMAGE_URL}
                                    alt={image?.Description}
                                    layout="fill"
                                    objectFit="cover"
                                    className={cx("image")}
                                />
                            )}
                        </div>
                    </div>
                    <div className={cx("actionContainer")}>
                        <div className={cx("messageContainer")}>
                            <div className={cx("heading")}>{fourTenErrorContent.title}</div>
                            <div className={cx("subheading")}>{fourTenErrorContent.copy}</div>
                            <div className={cx("buttonWrap")}>
                                <Button
                                    theme={"primary"}
                                    className={cx("button", "buttonText")}
                                    href={browseUrl}
                                    id="vehicle-not-available-browse-vehicles-cta"
                                >
                                    Browse {formattedVehicle.make} {formattedVehicle.model}
                                </Button>
                            </div>
                        </div>
                        <div className={cx("sep")}></div>
                        <div className={cx("ctaContainer")}>
                            <div className={cx("heading")}>{fourTenErrorContent.ctaHeading}</div>
                            <div className={cx("ctaWrap")}>
                                <Button
                                    theme={"secondary"}
                                    className={cx("button", "buttonText", "normalText")}
                                    href={""}
                                    id="vehicle-not-available-phone-cta"
                                >
                                    <DynamicPhoneNumber
                                        isLink
                                        className={"phoneNumber"}
                                        iconName={"Tel"}
                                    />
                                </Button>
                                <Button
                                    theme={contactUsCta.priority || "secondary"}
                                    className={cx("button", "buttonText", "normalText")}
                                    href={enquireUrl}
                                    id="vehicle-not-available-contact-us-cta"
                                    target={contactUsCta.target}
                                >
                                    <Image
                                        className={cx("iconContainer")}
                                        src={fourTenErrorContent.contactUsCtaIcon}
                                        alt={""}
                                        width={20}
                                        height={20}
                                    />
                                    {contactUsCta.text}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={cx("sep", "largeOnly")}></div>
                <div className={cx("recomendedVehiclesContainer")}>
                    <ModalProvider>
                        <RecommendedVehicles
                            stockBookNumber={vehicle.StockBookNumber}
                            shortlistedStocks={shortlist?.stockNumbers ?? []}
                            components={props.fourTenErrorContent.components}
                            allStoresDetails={page.allStoresDetails}
                            vehicleCardSettings={props.vehicleCardSettings}
                            vehicleDisplaySettings={page.vehicleDisplaySettings}
                        />
                    </ModalProvider>
                </div>
            </div>
        </Layout>
    );
};

export default VehicleNotFound;
